import { Tooltip } from '@pickleballinc/react-ui';
import clsx from 'clsx';

import {
  formatValueForH2H,
  formatValueForPlace,
  formatValueForPTS,
  formatValueForTables,
} from '@/modules/league/details/standings/utils';
import {
  PlayersOnStandings,
  SessionLeagueDetails,
  StandingsColumn,
} from '@/modules/league/new-components/types';

interface PlayerDataRowProps {
  session: SessionLeagueDetails;
  player: PlayersOnStandings;
  column: StandingsColumn;
}

export default function PlayerDataRow({ session, player, column }: PlayerDataRowProps) {
  if (!column.primaryDataKey) return null;

  const value = player[column.primaryDataKey];

  const renderMaxMvmt = () => (
    <>
      {value === true ? 'Y' : 'N'}
      {column.secondaryDataKeys?.[0] &&
      player[column.secondaryDataKeys?.[0]] === true &&
      value === false ? (
        <span className='text-red-600 font-semibold'>*</span>
      ) : null}
    </>
  );

  const renderPenalty = () => {
    const formatted = typeof value === 'string' ? formatValueForTables(value) : 0;
    return formatted === '10.0000' ? '0' : formatted;
  };

  const renderCurDay = () => (value === true ? 'Y' : 'N');

  const renderH2H = () =>
    column.primaryDataKey ? formatValueForH2H(session, player, column.primaryDataKey) : null;

  const renderDiffPercent = () =>
    player.TeamPointDifferencePercentage
      ? formatValueForTables(player.TeamPointDifferencePercentage)
      : formatValueForTables(player.TotalPoints_Difference_EarnedPossible_Percentage, 100);

  const renderWins = () => (
    <>
      {player.TotalWins}
      {player.TotalWins > 0 &&
      !session.IsReadOnlyMode &&
      !session.DateCompleteAndLock &&
      player.UseHeadToHeadWins !== false ? (
        <Tooltip placement='top-end'>
          <Tooltip.Trigger asChild>
            <label className='relative -top-3 left-1 items-center justify-center rounded-md border border-leagues-500 bg-leagues-500 px-2 text-white'>
              <span>{player.TeamHeadToHeadWins}</span>
            </label>
          </Tooltip.Trigger>
          <Tooltip.Content className='w-42 rounded bg-black bg-black/75 p-2 text-sm text-white'>
            Head to head wins within the tied win column.
          </Tooltip.Content>
        </Tooltip>
      ) : null}
    </>
  );

  const renderDefault = () => value;

  const renderContent = () => {
    switch (column.label) {
      case 'Rating':
        return <>{typeof value === 'number' ? value.toFixed(3) : null}</>;
      case 'Game Pts':
      case 'Match Pts':
      case 'Str8 Pts':
        return formatValueForPTS(session, player, column);

      case 'Max Mvmt':
        return renderMaxMvmt();

      case 'Group Bonus':
        return <>{typeof value === 'string' ? formatValueForTables(value) : null}</>;

      case 'E/P Diff %':
      case 'E/P Diff % - Penalty':
        return <>{typeof value === 'string' ? formatValueForTables(value, 100) : null}%</>;

      case 'Penalty':
        return renderPenalty();

      case 'Miss Cur Game Day':
        return renderCurDay();

      case 'H2H Wins':
      case 'H2H Diff':
        return renderH2H();

      case 'Game Day Place':
        return formatValueForPlace(player);

      case 'Diff %':
        return renderDiffPercent();

      case 'Wins':
        if (column.secondaryDataKeys) return renderWins();
        return renderDefault();

      default:
        return renderDefault();
    }
  };

  return (
    <div
      className={clsx(
        'inline-flex shrink-0 justify-center break-words border-r px-2 py-4 text-sm',
        column.width
      )}
    >
      {column.primaryDataKey ? <span className='self-center'>{renderContent()}</span> : null}
    </div>
  );
}
